import { lazy, Suspense, useEffect, useState } from "react";
import { Loader, LucideProps, AlertCircle } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import { searchIcons } from "@packages/api/madlenAPI";

const fallback = <div style={{ background: "#ddd", width: 24, height: 24 }} />;

interface IconProps extends Omit<LucideProps, "ref"> {
  name: keyof typeof dynamicIconImports | string;
}

const findIconKey = (name: string): keyof typeof dynamicIconImports | null => {
  if (!name) return null;
  const lowerCaseName = name.toLowerCase();
  for (const key in dynamicIconImports) {
    if (key.toLowerCase() === lowerCaseName) {
      return key as keyof typeof dynamicIconImports;
    }
  }
  return null;
};

const LucideIconWrapper = ({ name, ...props }: IconProps) => {
  const actualIconKey = findIconKey(name as string);

  if (!actualIconKey) {
    // console.error(`Invalid or non-existent Lucide icon name provided: ${name}`); // Keep error log but remove console
    return null;
  }

  const LucideIcon = lazy(dynamicIconImports[actualIconKey]);

  return (
    <Suspense fallback={fallback}>
      <LucideIcon {...props} />
    </Suspense>
  );
};

const NetworkIcon = ({
  value,
  type,
  ...props
}: { value: string; type: "LUCIDE" } & LucideProps) => {
  const [icon, setIcon] = useState<string | null>(null);
  // Removed loggedKeys state

  useEffect(() => {
    // Removed icon keys log

    let isMounted = true;
    if (type === "LUCIDE") {
      searchIcons({ description: value, iconPack: "LUCIDE" })
        .then((icons) => {
          if (isMounted) {
            const foundIconId = icons?.[0]?.iconId;
            if (foundIconId) {
              setIcon(foundIconId);
            } else {
              console.warn(
                `No valid LUCIDE icon found for description: ${value}`,
              );
              setIcon(null);
            }
          }
        })
        .catch((error) => {
          if (isMounted) {
            console.error(`Error searching for LUCIDE icon: ${value}`, error);
            setIcon(null);
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [value, type]);

  if (icon === undefined || icon === null) {
    return (
      <Loader
        className={`animate-spin ${props.className || ""}`}
        style={props.style}
      />
    );
  }

  if (type === "LUCIDE") {
    return <LucideIconWrapper name={icon} {...props} />;
  }

  return null;
};

export default NetworkIcon;
